import classNames from "classnames";
import { SearchInput } from "components/Atoms/SearchInput";
import SubLabel from "components/Atoms/Sublabel";
import CultureFlag from "components/Culture/CultureFlag";
import LimitedList from "components/LimitedList";
import { Loading } from "components/panels";
import React from "react";
import { NoDataAvailableDiv } from "../NoDataAvailableDiv";
import { useAddressbookSearch } from "./hooks";

interface Props {
  suggestedApiInstruction: server.dto.ListContactAddressBookToItemInstruction
  onContactClick: (entry: server.dto.ContactAddressBookEntry, email: string) => void
}

interface HoveringOverEntry {
  entry: server.dto.ContactAddressBookEntry
  email: string
}

const EmailAddressSearch = (props: React.PropsWithChildren<Props>) => {
  const addressBookSearch = useAddressbookSearch({ suggestedApiInstruction: props.suggestedApiInstruction });
  const [hoveringOver, setHoveringOver] = React.useState<HoveringOverEntry>(undefined);

  const onClick = () => {
    props.onContactClick(hoveringOver.entry, hoveringOver.email ?? "");
  };

  const getEmailClasses = (entry: HoveringOverEntry) => {
    if (hoveringOver == null) {
      return "padding";
    }

    return classNames("padding", {
      highlighted: hoveringOver.entry.contactId === entry.entry.contactId && hoveringOver.email === entry.email
    });
  };

  const getCompanyNameLabel = (entry: server.dto.ContactAddressBookEntry) => {
    return (<LimitedList
      items={entry.companies}
      renderItem={company => <span key={company.companyId}>{company.name}</span>}
      renderExcess={nr => <span className="margin-left-small">{`(+${nr})`}</span>}
      limit={1}
      delimiter=""
    />);
  };

  return <>
    <div>
      <SearchInput
        value={addressBookSearch.searchInput}
        placeholder={RESX.Contact.KeywordPlaceholder}
        onChange={(_, { value }) => { addressBookSearch.setSearchInput(value); }}
        focusOnMount
      />
    </div>

    <div className="scrollbox-auto grow1 flex-column gap-small" >
      {!addressBookSearch.isLoading && addressBookSearch.results.length > 0 && <>
        {addressBookSearch.results.map((item, idx) =>
          <div
            ref={addressBookSearch.shouldIncludeVisibilityRef(idx) ? addressBookSearch.ref : undefined}
            style={{ cursor: "pointer" }}
            className="padding gray-100 radius"
            key={item.contactId}
            onClick={onClick}
            onMouseEnter={() => { setHoveringOver({ entry: item, email: item.emails[0] ?? "" }); }}
            onMouseLeave={() => { setHoveringOver(undefined); }}
          >
            <div className="flex padding">
              <div className="grow1">
                <div className="margin-bottom-mini"><strong>{item.name.fullName}</strong></div>
                <SubLabel>{item.position}</SubLabel>
              </div>

              <div>
                <CultureFlag lcid={item.culture} size="medium" />
              </div>
            </div>

            <div className="margin-small" style={{ borderBottom: "1px solid #BBB" }} />

            <div className="flex">
              <div style={{ flex: 1 }} className="flex-column">
                {item.emails.map((email) =>
                  <div key={email}>
                    <div
                      style={{ display: "inline-block", textDecoration: "underline" }}
                      key={email}
                      className={getEmailClasses({ entry: item, email })}
                      onMouseEnter={() => { setHoveringOver({ entry: item, email }); }}
                      onMouseLeave={() => { setHoveringOver({ entry: item, email: item.emails[0] ?? "" }); }}
                    >
                      {email}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ flex: 1.5 }} className="padding">
                {item.companies[0] != null && <>
                  <div>{getCompanyNameLabel(item)}</div>
                  <div className="text-sublabel">{item.companies[0].cityCountry}</div>
                </>}
              </div>

            </div>
          </div>)}
      </>}

      {!addressBookSearch.isLoading && addressBookSearch.results.length === 0 && <div>
        <NoDataAvailableDiv />
      </div>}

      {addressBookSearch.isLoading && <Loading />}
    </div>

    {props.children}
  </>;
};

export default EmailAddressSearch;